import { useEffect, useState, useCallback } from "react";
import { Smartphone, Tablet, Trash2, Loader2, Wifi, WifiOff, RefreshCw } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { toast } from "sonner";
import api from "../../lib/api";

const PLATFORM_META = {
  ios: { label: "iOS", icon: Smartphone, tint: "bg-muted/50 text-foreground border-border" },
  android: { label: "Android", icon: Smartphone, tint: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  web: { label: "Web", icon: Tablet, tint: "bg-sky-50 text-sky-700 border-sky-200" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = (Date.now() - then) / 1000;
  if (diff < 60) return "hace unos segundos";
  if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
  const days = Math.floor(diff / 86400);
  if (days < 30) return `hace ${days} día${days === 1 ? "" : "s"}`;
  return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

export default function ConnectedDevicesCard() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revoking, setRevoking] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.getPushDevices();
      setDevices(res.devices || []);
    } catch (err) {
      toast.error(err.message || "No se pudieron cargar los dispositivos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRevoke = async (tokenId, name) => {
    if (!window.confirm(`¿Revocar acceso push a ${name || "este dispositivo"}? Dejará de recibir notificaciones en el móvil.`)) return;
    setRevoking(tokenId);
    try {
      await api.revokePushDevice(tokenId);
      toast.success("Dispositivo revocado");
      await load();
    } catch (err) {
      toast.error(err.message || "No se pudo revocar el dispositivo");
    } finally {
      setRevoking(null);
    }
  };

  const active = devices.filter(d => d.active);
  const revoked = devices.filter(d => !d.active);

  return (
    <Card className="p-6 space-y-4" data-testid="connected-devices-card">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg font-semibold flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-primary" />
            Dispositivos móviles conectados
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            Móviles y tablets con la app MenuGenius que reciben notificaciones push de este panel.
          </p>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          data-testid="devices-refresh-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : devices.length === 0 ? (
        <div className="text-center py-10 border border-dashed rounded-lg bg-muted/50/40">
          <Smartphone className="w-8 h-8 mx-auto text-muted-foreground/60 mb-2" />
          <p className="text-sm text-muted-foreground">
            Todavía no has instalado la app móvil en ningún dispositivo.
          </p>
          <p className="text-xs text-muted-foreground/80 mt-1">
            Descarga MenuGenius desde la App Store o Google Play para recibir alertas en el móvil.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {active.length > 0 && (
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Wifi className="w-3 h-3 text-emerald-700" /> Activos ({active.length})
              </div>
              {active.map(d => {
                const meta = PLATFORM_META[d.platform] || PLATFORM_META.ios;
                const Icon = meta.icon;
                return (
                  <div
                    key={d.token_id}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-slate-50/50 transition-colors"
                    data-testid={`device-row-${d.token_id}`}
                  >
                    <div className={`p-2 rounded-md border ${meta.tint}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">
                          {d.device_name || "Dispositivo sin nombre"}
                        </span>
                        <Badge variant="outline" className={`text-xs ${meta.tint}`}>
                          {meta.label}
                        </Badge>
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        Último uso: {timeAgo(d.last_seen_at) || "—"}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-700 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleRevoke(d.token_id, d.device_name)}
                      disabled={revoking === d.token_id}
                      data-testid={`device-revoke-${d.token_id}`}
                    >
                      {revoking === d.token_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-1" />
                          Revocar
                        </>
                      )}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {revoked.length > 0 && (
            <details className="group">
              <summary className="cursor-pointer text-xs font-medium uppercase tracking-wide text-muted-foreground flex items-center gap-1.5 py-2 select-none">
                <WifiOff className="w-3 h-3" />
                Revocados ({revoked.length})
                <span className="text-[10px] normal-case font-normal">· click para ver</span>
              </summary>
              <div className="space-y-2 mt-2">
                {revoked.map(d => {
                  const meta = PLATFORM_META[d.platform] || PLATFORM_META.ios;
                  const Icon = meta.icon;
                  return (
                    <div
                      key={d.token_id}
                      className="flex items-center gap-3 p-3 rounded-lg border bg-muted/50/40 opacity-70"
                      data-testid={`device-row-revoked-${d.token_id}`}
                    >
                      <div className="p-2 rounded-md border bg-muted text-muted-foreground border-border">
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-sm truncate line-through text-muted-foreground">
                            {d.device_name || "Dispositivo sin nombre"}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            Revocado
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Revocado {timeAgo(d.deactivated_at) || "—"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}
